/***********************************************************************************************************************
 *                                                                                                                     *
 * V.1.2                                                                                                               *
 * User: Livnat Oren                                                                                              *
 * Date: 6/03/2014
 *
 * Add trigger to the oblur event
 * Add trigger to the enter event
 * Add validation to integer numbers
 * *
 * V1.0                                                                                                                *
 *                                                                                                                     *
 * User: Adi Baron                                                                                                     *
 * Date: 8/5/13                                                                                                        *
 * Visual PDF:                                                                                                         *
 *      https://docs.google.com/a/liveperson.com/file/d/0B8IBNMmJWXC2dEJ0SkFNV0tReWs/edit                              *
 *      https://docs.google.com/a/liveperson.com/file/d/0B8IBNMmJWXC2MmlCeHMyLWV4Umc/edit                              *
 *                                                                                                                     *
 * ********************************************************************************************************************/

define(/** @lends InputField */ function (require) {

	"use strict";

	/////////////////////////////////////////////////////////////////////////////
	//                              Dependencies                               //
	/////////////////////////////////////////////////////////////////////////////
	var _ = require("underscore");
	var $ = require("jquery");
	var Marionette = require("marionette");
	var Modernizr = require('modernizr');
	var Bubble = require("ui/bubble/Bubble");
	var template = require('ui/input-field/templates/InputField.tmpl');
	var InputFieldModel = require('ui/input-field/js/models/InputFieldModel');
	var EMPTY_VALUE = "";
	var Translator = require("i18n/translator");
	require('ui.plugins/jquery.inputCursor');

	/**
	 * Creates a new instance of the InputField class.
	 *
	 * @example
	 * var InputField = require('ui/input-field/InputField');
	 *
	 * var fNameInput = new InputField({
	 *     el: this.ui.fNameInputPlaceholder
	 * });
	 *
	 * @constructor InputField
	 *
	 * @param {InputField.Options} options
	 * @throws {Error} An error if the given options object is invalid.
	 *
	 * @requires backbone
	 * @requires marionette
	 * @requires underscore
	 *
	 * @augments Marionette.ItemView
	 *
	 * @classdesc A basic text field that is used as a replacement for traditional HTML text inputs. This component can
	 * also be used as the base class for more sophisticated input controls.
	 */
	var InputField = Marionette.ItemView.extend({
		errorMsg        :null,
		withUIComponents:true,
		placeholderIsSet:false,
		events          :{
			"blur input"   :function (e) {
				_onInputBlur(e, this);
			},
			"cut input"    :function () {
				handleDelayedChange(this);
			},
			"paste input"  :function () {
				handleDelayedChange(this);
			},
			"keydown input":function (e) {
				this.handleKeyDownEvent(e);
			},
			"keyup input"  :function (e) {
				handleChange(e, this);
			},
			"focus input"  :function () {
				_onInputFocus(this);
			},
            "clickoutside" : function(e) {
                _handleClickOutside(e, this);
            }
		},

		ui         :{
			input:"input"
		},
		modelEvents:{
			"change:enabled":"renderEnabled",
			"change:readonly":"renderReadonly"
		},

		initialize:function (options) {
			// make sure options is a valid object.
			if (!options || !_.isPlainObject(options)) {
				throw "InputField :: invalid argument: options is illegal.";
			}

			if (options.el) {
				options.placeholder = !_.isUndefined(this.$el.data('placeholder')) ? this.$el.data('placeholder') : options.placeholder;
			}

			options.value = !_.isUndefined(options.value) ? options.value : '';
			options.placeholder = !_.isUndefined(options.placeholder) ? options.placeholder : '';

			var inputFieldModel = new InputFieldModel();
			inputFieldModel.set(options);
			this.errorPosition = options.errorPosition || InputField.ERROR_POSITION.BOTTOM;
			this.errorAlign = options.errorAlign || InputField.ERROR_ALIGN.MIDDLE;
			this.zIndexBubble = options.zIndexBubble || Bubble.DEFAULT_ZINDEX;
			this.permutation = options.permutation || InputField.Permutation.B;
            this.theme = options.theme || InputField.THEME.DEFAULT;
			this.inputType = options.inputType;
			this.template = options.template || template;
			this.model = inputFieldModel;
			this.ariaLabel = options.ariaLabel;
			this.listenTo(this.model, 'change:' + InputField.VALUE, this.show);
			this.listenTo(this.model, 'change:placeholder', this.show);

			this.autoFocus = options.autoFocus || false;
			this.disabled = options.disabled || false;
            this.lastKeyDown = null;
            this.range = options.range || {min:1,max:100};
            this.hideErrorMsg = options.hideErrorMsg;

            if (isNaN(this.range.min) || isNaN(this.range.max) || this.range.max < this.range.min) {
                throw new Error("range value are  illegal");
            }
		},

		onRender:function () {
			this.ui.input.attr('class', "input-field permutation" + this.permutation);
            this.ui.input.addClass(this.theme);
			this.show();
			this.setFocus(this.autoFocus);
		},

		show:function () {
			var val = this.getValue();

			if (this.ui.input.val() !== val) {
				this.ui.input.val(val);
			}


			if (EMPTY_VALUE === this.getValue()) {
				this.showPlaceholder();
			}
            else {
                this.hidePlaceholder({silent:true});
            }

		},

		/**
		 * Get input value.
		 */
		getValue:function () {
			return this.model.get(InputField.VALUE);
		},

		/**
		 * Set input value.
		 *
		 * @param: value for input field.
		 */
		setValue:function (value, options) {
			options = options || {};
			options.silent = _.has(options, "silent") ? options.silent : true;

			this._setValue(value, options);
		},

		_setValue:function (value, options) {
			options = options || {};

			this.model.set(InputField.VALUE, String(value));
			//input is converting automatically the value to string, to avoid unneccesssary model change events we keep it as string too

			if (!options.silent) {
				this.trigger(InputField.TRIGGERING.CHANGE, this.getValue());
			}
		},

		/**
		 * Set input placeholder.
		 *
		 * @param: placeholder for input field.
		 */
		setPlaceholder:function (val) {
			this.ui.input.attr('placeholder', val);
			this.model.set({"placeholder":val});
		},

		setError:function (isError, errorMsg) {
			if (isError === true) {
				this.errorMsg = errorMsg;
				this.ui.input.addClass("error");
			}
			else {
				if (typeof this.ui.input === "object" && "removeClass" in this.ui.input) { // TODO (Gal) : There is a case (In entering personView) that we arrive to here when the InputField is not rendered so this.ui.input is a string and so we do not have removeClass method
					this.ui.input.removeClass("error");
					this.errorMsg = null;
					if (this.uiComponents && this.uiComponents.InputFieldError) {
						this.uiComponents.InputFieldError.close();
					}
				}
			}
		},

		showPlaceholder:function () {
			if (!Modernizr.input.placeholder && !this.ui.input.is(":focus")) {
				if (!this.placeholderIsSet) {
					this.placeholderIsSet = true;
					this.ui.input.addClass('placeholder');
				}
				this.ui.input.val(this.ui.input.attr('placeholder'));
			}
		},

		hidePlaceholder:function (options) {
            options = options || {};
			if (!Modernizr.input.placeholder) {
				if (this.placeholderIsSet) {
					this.ui.input.removeClass('placeholder');
					this.placeholderIsSet = false;
                    if (!options.silent) {
                        this.ui.input.val(EMPTY_VALUE);
                    }
				}
			}
		},
		/**
		 * set the state of the inputfield to enabled/disabled
		 * @param {true / false} For inputfield enabled - true, otherwise - false.
		 */
		setEnabled     :function (enabled) {
			this.model.set("enabled", enabled);
		},

		renderEnabled:function (model, enabled) {
			this.ui.input.prop("disabled", !enabled);
		},
		/**
		 * @return {true / false} -  For checkbox enabled - true, otherwise - false.
		 */
		isEnabled    :function () {
			return this.model.get("enabled");
		},

        /**
         * set the state of the inputfield to readonly
         * @param {true / false} For inputfield readonly - true, otherwise - false.
         */
        setReadonly: function(readonly) {
            this.model.set("readonly", readonly);
        },

        renderReadonly:function (model, readonly) {
            this.ui.input.prop("readonly", readonly);
        },
        isReadonly: function () {
            return this.model.get("readonly");
        },

		setFocus        :function (focus) {
			if (focus) {
				setTimeout(_.bind(function () {
					this._focusInput();
				}, this), 1);
			}
		},

        select: function() {
            if (this.ui.input) {
                this.ui.input.select();
            }
        },

        blurInput: function() {
            this.ui.input.blur();
        },

        handleKeyDownEvent:function (e) {
            this.lastKeyDown = (e && e.keyCode) ? e.keyCode : null;

			if (e && e.keyCode === $.ui.keyCode.ENTER) {
				this.trigger(InputField.TRIGGERING.ENTER_PRESSED, {
					value: this.getValue(),
					event: e
				});
			}

            if (e && (e.keyCode === $.ui.keyCode.UP || e.keyCode === $.ui.keyCode.DOWN)) {
                this.trigger(InputField.TRIGGERING.ARROW_PRESSED, {
                    isUp: (e.keyCode === $.ui.keyCode.UP),
                    value: this.getValue(),
                    event: e
                });
            }
		},
		_focusInput     :function () {
            if (this.ui.input && this.ui.input.inputcursor) {
                if (this.model.get("id")) {
                    this.ui.input.inputcursor('focusEnd');
                }
                else {
                    this.ui.input.inputcursor('selectAll');
                }
            }
		},

		isValidInputType:function (val) {
			var isOk = true;

			if (_.isUndefined(this.inputType)) {
				return isOk;
			}

			switch (this.inputType) {
				case (this.InputTypeEnum.Number):
					isOk = this.isValidInputTypeNumber(val);
					break;
				case (this.InputTypeEnum.PositiveNumber):
					isOk = this.isValidInputTypePositiveNumber(val);
					break;
                case (this.InputTypeEnum.numbersBetweenMinToMax):
					isOk = this.isValidInputnumbersBetweenMinToMax(val);
					break;
				case (this.InputTypeEnum.PositiveNumberWithZero):
					isOk = this.isValidInputTypePositiveNumberWithZero(val);
					break;
				case (this.InputTypeEnum.NaturalNumber):
					isOk = this.isValidInputTypeNaturalNumber(val);
					break;
                case (this.InputTypeEnum.PositiveNumberWithZeroAndTwoNumAfterDecimal):
                    isOk = this.isValidInputTypePositiveNumberWithZeroAndTwoNumAfterDecimal(val);
                    break;
                case (this.InputTypeEnum.WholeNumber):
                    isOk = this.isValidInputTypeWholeNumber(val);
                    break;
                case (this.InputTypeEnum.NumberWithZeroAndTwoNumAfterDecimal):
                    isOk = this.isValidInputTypeNumberWithZeroAndTwoNumAfterDecimal(val);
                    break;
			}

			return isOk;
		},

		isValidInputTypeNumber :function (val) {
			if ((val.length === 0) || //allow inputfield values to be deleted
				(val.length === 1 && val === "-") || //allow - at start of number
				!isNaN(val)) {   //allow number
				return true;
			}
			else {
				this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.numbersOnly"));
				this.setErrorBubble();
				return false;
			}

		},
		isValidInputTypePositiveNumber        :function (val) {
			if ((val.length === 0) || //allow inputfield values to be deleted
				(!isNaN(val) && (val * 1) > 0)) { //allow only positives
				return true;
			}
			else {
				this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.positiveNumbersOnly"));
				this.setErrorBubble();
				return false;
			}
		},
		isValidInputTypePositiveNumberWithZero:function (val) {
			if ((val.length === 0) || //allow inputfield values to be deleted
                (!isNaN(val) && (val !== " ") && (val ) >= 0)) { //allow only positives and zero (whitespace equals 0 so we need to make sure the value is not a whitespace)
				return true;
			}
			else {
				this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.positiveNumbersOnlyWithZero"));
				this.setErrorBubble();
				return false;
			}
		},
        isValidInputnumbersBetweenMinToMax:function (val) {

            if ((val.length === 0) || //allow inputfield values to be deleted
                (!isNaN(val) && (val !== " ") && (val ) >= this.range.min && (val ) <= this.range.max)) { //allow only positives and zero (whitespace equals 0 so we need to make sure the value is not a whitespace)
                return true;
            }
            else {
                this.setError(true,Translator.translate({key: "LEUIComponents.errors.illegalInput.numbersBetweenMinToMax",args: [this.range.min,this.range.max]}));
                this.setErrorBubble();
                return false;
            }
        },
        isValidInputTypePositiveNumberWithZeroAndTwoNumAfterDecimal:function (val) {
            var pattern = /^(\d+\.?\d{0,2})$/;
            if ((val.length === 0) || //allow inputfield values to be deleted
                (!isNaN(val) && (val ) >= 0 && pattern.test(val))) { //allow only positives with up to 2 numbers after decimal point and zero
                return true;
            }
            else {
                this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.positiveNumbersOnlyWithZeroAndTwoNumAfterDecimal"));
                this.setErrorBubble();
                return false;
            }
        },
        isValidInputTypeNumberWithZeroAndTwoNumAfterDecimal:function (val) {
            var pattern = /^(\-?\d+\.?\d{0,2})$/;
            if ((val.length === 0) || //allow input field values to be deleted
                (val.length === 1 && val === "-") || //allow - at start of number
                (!isNaN(val) && pattern.test(val))) { //allow up to 2 numbers after decimal point and zero
                return true;
            }
            else {
                this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.numbersOnlyWithZeroAndTwoNumAfterDecimal"));
                this.setErrorBubble();
                return false;
            }
        },
        isValidInputTypeWholeNumber:function (val) {
            var pattern = /^(\d*)$/;
            if ((val.length === 0) ||
                (!isNaN(val) && pattern.test(val))) {
                return true;
            }
            else {
                this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.wholeNumbersOnlyWithZero"));
                this.setErrorBubble();
                return false;
            }
        },
		isValidInputTypeNaturalNumber:function (val) {
			var pattern = /^([1-9]\d*)$/;
			if ((val.length === 0) ||
				(!isNaN(val) && pattern.test(val))) {
				return true;
			}
			else {
				this.setError(true, Translator.translate("LEUIComponents.errors.illegalInput.naturalNumber"));
				this.setErrorBubble();
				return false;
			}
		},
		InputTypeEnum                                       :{
			"Number"                                        :0,
			"PositiveNumber"                                :1,
			"PositiveNumberWithZero"                        :2,
			"NaturalNumber"                                 :3,
            "PositiveNumberWithZeroAndTwoNumAfterDecimal"   :4,
            "WholeNumber"                                   :5,
            "NumberWithZeroAndTwoNumAfterDecimal"           :6,
            "numbersBetweenMinToMax"                        :7
		},
		setErrorBubble                        :function () {
			if (this.ui.input.hasClass("error") && this.errorMsg && !this.uiComponents.InputFieldError && !this.hideErrorMsg) {
				this.uiComponents.host$.createAndRender("InputFieldError", {
					type   :Bubble,
					options:{
						targetControl  :this.ui.input,
						text           :this.errorMsg,
						position       :this.errorPosition,
						align          :this.errorAlign,
                        zIndex         :this.zIndexBubble,
						icon           :Bubble.ICON.ERROR,
						showCloseButton:false
					}
				});
			}
			this.hidePlaceholder();
		}

	}); // EO InputField

	/**
	 * Triggered when the input is on focus
	 */

    function _handleClickOutside(e, view) {
        view.trigger(InputField.TRIGGERING.ONCLICKOUTSIDE);
    }

	function _onInputFocus(view) {
		view.setErrorBubble();
        view.lastKeyDown = null;
        view.trigger(InputField.TRIGGERING.ONFOCUS);
	}

	/**
	 * Triggered when the input is blur
	 */
	function _onInputBlur(event, view) {
		view.setError(false);

		var trimmedValue = view.ui.input.val().trim();
		if (trimmedValue !== view.getValue()) {
			view._setValue(trimmedValue);
		}
		else {
			view.show();
		}
        view.trigger(InputField.TRIGGERING.ONBLUR, view.getValue(), {lastKeyDown: view.lastKeyDown, event:event}); // First this was just after the Error but when the value was empty the trigger crashes the method. We moved it to here so it's the last line of code
	}

	function handleDelayedChange(inputField) {
		setTimeout(function () { //right click paste/cut value is available on the element only after timeout
			handleChange(null, inputField);
		}, 0);
	}

	function handleChange(e, inputField) {
		var val = inputField.ui.input.val();
		if (inputField.isValidInputType(val)) {
			inputField.setError(false);
			inputField.setValue.call(inputField, val, {silent: false});
		} else {
			inputField.ui.input.val(inputField.getValue()); //revert to last value saved on model
		}
	}

	InputField.Type = InputFieldModel.Type;

	/**
	 * The available view permutations of the input-fields.
	 *
	 * @enum
	 * @readonly
	 */
	InputField.Permutation = {
		A:'A', B:'B', C:'C', D:'D', E:'E', F:'F', G:'G', H:'H', I:'I', I2:'I2', J:'J', J2:'J2', K:'K', L:"L", M:"M", N:"N", O:"O", O2:"O2", "P":"P", "MIN":"MIN","R":"R"
	}; // EO InputField.Type
    InputField.THEME = {
        DEFAULT: "default-theme",
        NIGHT: "night-theme"
    };
	InputField.TRIGGERING = {
		CHANGE       :"change",
        ARROW_PRESSED:"arrowPressed",
		ENTER_PRESSED:"enterPressed",
        ONFOCUS: "onfocus",
		ONBLUR: "onblur",
        ONCLICKOUTSIDE: "onclickoutside"
	};
	InputField.ERROR_POSITION = Bubble.POSITION;
	InputField.ERROR_ALIGN = Bubble.ALIGN;
	InputField.VALUE = "value";

	// return the input-field component.
	return InputField;
});
